import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from './components/Header';
import Hero from './components/Hero';
import Features from './components/Features';
import HowItWorks from './components/HowItWorks';
import TrustedBy from './components/TrustedBy';
import Footer from './components/Footer';
import { sanitizeInput } from './utils/securityUtils';
import { initGA4VirtualPageViews } from './utils/analytics';
import { getSlug, getSectionFromSlug } from './utils/slugs';
import './App.css';

function App() {
  const { i18n } = useTranslation();
  
  const metaTitles = {
    en: sanitizeInput("CookiePal | Cheapest & Fastest Cookie Banner"),
    de: sanitizeInput("CookiePal | Günstigster & Schnellster Cookie Banner"),
    fr: sanitizeInput("CookiePal | Bannière de Cookies la Moins Chère & la Plus Rapide"),
    es: sanitizeInput("CookiePal | Banner de Cookies más Barato y Rápido"),
    it: sanitizeInput("CookiePal | Banner dei Cookie più Economico e Veloce"),
    pl: sanitizeInput("CookiePal | Najtańszy i Najszybszy Baner Ciasteczek"),
    sv: sanitizeInput("CookiePal | Billigaste & Snabbaste Cookie Banner"),
    da: sanitizeInput("CookiePal | Billigste & Hurtigste Cookie Banner"),
    pt: sanitizeInput("CookiePal | Banner de Cookies mais Barato e Rápido"),
    fi: sanitizeInput("CookiePal | Halvin ja Nopein Cookie Banner"),
    el: sanitizeInput("CookiePal | Φθηνότερο & Γρηγορότερο Banner Cookie"),
    cs: sanitizeInput("CookiePal | Nejlevnější a Nejrychlejší Cookie Banner"),
    nl: sanitizeInput("CookiePal | Goedkoopste & Snelste Cookie Banner")
  };

  const metaDescriptions = {
    en: sanitizeInput("Save time and money with the easiest cookie banner. Free up to 10,000 views. Setup in 5 minutes. Try without payment method!"),
    de: sanitizeInput("Sparen Sie Zeit und Geld mit dem einfachsten Cookie-Banner. Kostenlos bis 10.000 Aufrufe. Einrichtung in 5 Minuten. Ohne Zahlungsmethode testen!"),
    fr: sanitizeInput("Économisez du temps et de l'argent avec la bannière de cookies la plus simple. Gratuit jusqu'à 10 000 vues. Installation en 5 minutes. Essayez sans méthode de paiement !"),
    es: sanitizeInput("Ahorre tiempo y dinero con el banner de cookies más sencillo. Gratis hasta 10,000 vistas. Configuración en 5 minutos. ¡Pruebe sin método de pago!"),
    it: sanitizeInput("Risparmia tempo e denaro con il banner dei cookie più semplice. Gratuito fino a 10.000 visualizzazioni. Configurazione in 5 minuti. Prova senza metodo di pagamento!"),
    pl: sanitizeInput("Oszczędzaj czas i pieniądze dzięki najprostszemu banerowi ciasteczek. Darmowy do 10 000 wyświetleń. Konfiguracja w 5 minut. Wypróbuj bez metody płatności!"),
    sv: sanitizeInput("Spara tid och pengar med den enklaste cookie-bannern. Gratis upp till 10 000 visningar. Uppställning på 5 minuter. Prova utan betalningsmetod!"),
    da: sanitizeInput("Spar tid og penge med den nemmeste cookie-banner. Gratis op til 10.000 visninger. Opsætning på 5 minutter. Prøv uden betalingsmetode!"),
    pt: sanitizeInput("Economize tempo e dinheiro com o banner de cookies mais simples. Grátis até 10.000 visualizações. Configuração em 5 minutos. Experimente sem método de pagamento!"),
    fi: sanitizeInput("Säästä aikaa ja rahaa helpoimmalla cookie-bannerilla. Ilmainen jopa 10 000 katselukertaan asti. Asetus 5 minuutissa. Kokeile ilman maksutapaa!"),
    el: sanitizeInput("Εξοικονομήστε χρόνο και χρήματα με το πιο απλό banner cookie. Δωρεάν έως 10.000 προβολές. Ρύθμιση σε 5 λεπτά. Δοκιμάστε χωρίς μέθοδο πληρωμής!"),
    cs: sanitizeInput("Ušetřete čas a peníze s nejjednodušším cookie bannerem. Zdarma až do 10 000 zobrazení. Nastavení za 5 minut. Vyzkoušejte bez platební metody!"),
    nl: sanitizeInput("Bespaar tijd en geld met de eenvoudigste cookie banner. Gratis tot 10.000 views. Setup in 5 minuten. Probeer zonder betaalmethode!")
  };

  useEffect(() => {
    initGA4VirtualPageViews();

    try {
      const pathSegments = window.location.pathname.split('/').filter(Boolean);
      const supportedLanguages = ['en', 'de', 'fr', 'es', 'it', 'pl', 'sv', 'da', 'pt', 'fi', 'el', 'cs', 'nl'];
      
      let detectedLang = 'en';
      let slug = '';

      if (pathSegments.length > 0) {
        if (supportedLanguages.includes(pathSegments[0])) {
          detectedLang = pathSegments[0];
          slug = pathSegments[1] || '';
        } else {
          detectedLang = 'en';
          slug = pathSegments[0];
        }
      } else {
        const storedLang = localStorage.getItem('i18nextLng');
        if (storedLang && supportedLanguages.includes(storedLang)) {
          detectedLang = storedLang;
        }
      }
      
      if (i18n.language !== detectedLang) {
        i18n.changeLanguage(detectedLang);
      }
      localStorage.setItem('i18nextLng', detectedLang);

      const sectionId = getSectionFromSlug(detectedLang, slug);
      if (sectionId && sectionId !== 'home') {
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    } catch (error) {
      console.error("Error in language initialization:", error);
      i18n.changeLanguage('en');
    }
  }, []);

  // IntersectionObserver for tracking Scroll Navigation
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-40% 0px -60% 0px',
      threshold: 0
    };

    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          if (!sectionId) return;
          
          const currentLang = i18n.language;
          if (!currentLang) return;
          
          const slug = getSlug(currentLang, sectionId);
          let newPath = currentLang === 'en' ? `/${slug}` : `/${currentLang}/${slug}`;
          
          if (currentLang === 'en' && !slug) newPath = '/';
          
          if (window.location.pathname !== newPath) {
            window.history.replaceState(null, '', newPath);
          }
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    setTimeout(() => {
      const homeSection = document.getElementById('home');
      const featuresSection = document.getElementById('features');
      const howItWorksSection = document.getElementById('how-it-works');
      const pricingSection = document.getElementById('pricing'); // NIEUW: Prijzen sectie tracken
      
      if (homeSection) observer.observe(homeSection);
      if (featuresSection) observer.observe(featuresSection);
      if (howItWorksSection) observer.observe(howItWorksSection);
      if (pricingSection) observer.observe(pricingSection);
    }, 1000);

    return () => observer.disconnect();
  }, [i18n.language]);

  useEffect(() => {
    const handlePopState = () => {
      try {
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        const supportedLanguages = ['en', 'de', 'fr', 'es', 'it', 'pl', 'sv', 'da', 'pt', 'fi', 'el', 'cs', 'nl'];
        
        let detectedLang = 'en';
        let slug = '';

        if (pathSegments.length > 0) {
          if (supportedLanguages.includes(pathSegments[0])) {
            detectedLang = pathSegments[0];
            slug = pathSegments[1] || '';
          } else {
            detectedLang = 'en';
            slug = pathSegments[0];
          }
        }
        
        if (i18n.language !== detectedLang) {
          i18n.changeLanguage(detectedLang);
        }

        const sectionId = getSectionFromSlug(detectedLang, slug);
        if (sectionId) {
          const element = document.getElementById(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      } catch (error) {
        console.error("Error handling popstate:", error);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [i18n]);

  useEffect(() => {
    try {
      const currentLang = i18n.language;
      document.documentElement.lang = currentLang;
      document.title = metaTitles[currentLang] || metaTitles.en;
      
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = metaDescriptions[currentLang] || metaDescriptions.en;
      
      updateMetaTags(currentLang);
    } catch (error) {
      console.error("Error updating language:", error);
    }
  }, [i18n.language]);

  const updateMetaTags = (lang) => {
    try {
      const supportedLanguages = ['en', 'de', 'fr', 'es', 'it', 'pl', 'sv', 'da', 'pt', 'fi', 'el', 'cs', 'nl'];
      if (!supportedLanguages.includes(lang)) lang = 'en';
      
      let ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.content = metaTitles[lang] || metaTitles.en;
      
      let ogDescription = document.querySelector('meta[property="og:description"]');
      if (ogDescription) ogDescription.content = metaDescriptions[lang] || metaDescriptions.en;
      
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (twitterTitle) twitterTitle.content = metaTitles[lang] || metaTitles.en;
      
      let twitterDescription = document.querySelector('meta[name="twitter:description"]');
      if (twitterDescription) twitterDescription.content = metaDescriptions[lang] || metaDescriptions.en;
    } catch (error) {
      console.error("Error updating meta tags:", error);
    }
  };

  return (
    <div className="app-container">
      <Header />
      <Hero />
      <TrustedBy />
      <Features />
      <HowItWorks />
      <Footer />
    </div>
  );
}

export default App;